import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTIVE_GREEN, getStatusChipSx } from '../../constants/statusColors';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  PersonAddOutlined as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  PersonOutlined as PersonIcon,
  CheckCircleOutlined as CheckCircleIcon,
  BusinessOutlined as BusinessIcon,
  Download as DownloadIcon,
  CloudUploadOutlined as ImportIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import StatCard from '../../components/common/StatCard';
import FilterPanel from '../../components/common/FilterPanel';
import DataTable from '../../components/common/DataTable';
import CompanySecretaryImportReviewModal from '../../components/company-secretaries/CompanySecretaryImportReviewModal';
import EditGlobalPermissionsDialog from '../../components/permissions/EditGlobalPermissionsDialog';
import { csAdminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { canManageCompanySecretaries, canManagePortalAccountPermissions } from '../../constants/csAdminRoles';
import { mapCompanySecretaryFromApi, mapCompanyFromApi } from '../../utils/apiMappers';
import {
  downloadCompanySecretaryTemplate,
  parseImportedCompanySecretaryRow,
  validateImportedCompanySecretaryRow,
  calculateCompanySecretarySimilarityScore,
  buildCompanySecretaryCreatePayload,
  isEmptyCompanySecretaryRow,
  isCompanySecretaryTemplateHelperRow,
} from '../../utils/companySecretaryImportTemplate';

// Company Secretaries List Component
const CompanySecretariesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [secretaries, setSecretaries] = useState([]);
  const [filteredSecretaries, setFilteredSecretaries] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importedSecretaries, setImportedSecretaries] = useState([]);
  const [importing, setImporting] = useState(false);
  const [editPermissionsOpen, setEditPermissionsOpen] = useState(false);
  const [selectedSecretary, setSelectedSecretary] = useState(null);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    totalAssignments: 0,
  });

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');

  // Secretarial Admin and IT Admin may manage company secretary portal accounts
  useEffect(() => {
    if (!canManageCompanySecretaries(user?.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchSecretaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, companyFilter, secretaries]);

  const fetchSecretaries = async () => {
    try {
      setLoading(true);

      const response = await csAdminApi.getCompanySecretaries();
        const list = (response.data?.data || []).map(mapCompanySecretaryFromApi);
        setSecretaries(list);
        setSummary(
          response.data?.summary || {
            total: list.length,
            active: list.filter((s) => s.status === 'ACTIVE').length,
            totalAssignments: 0,
          }
        );
        setError('');
    } catch (err) {
      console.error('Error fetching company secretaries:', err);
      setError('Failed to load company secretaries');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...secretaries];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (secretary) =>
          secretary.name.toLowerCase().includes(query) ||
          secretary.email.toLowerCase().includes(query) ||
          secretary.companies.some((company) => company.toLowerCase().includes(query))
      );
    }

    // Company filter
    if (companyFilter) {
      filtered = filtered.filter((secretary) =>
        secretary.companies.includes(companyFilter)
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter((secretary) => secretary.status === statusFilter);
    }

    setFilteredSecretaries(filtered);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (filterName, value) => {
    if (filterName === 'company') {
      setCompanyFilter(value);
    } else if (filterName === 'status') {
      setStatusFilter(value);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setCompanyFilter('');
    setStatusFilter('');
  };

  const handleDownloadTemplate = () => {
    downloadCompanySecretaryTemplate();
    enqueueSnackbar('Template downloaded', { variant: 'success' });
  };

  const findMatchingSecretaries = (importedSecretary, existingSecretaries) => {
    return existingSecretaries
      .map((existing) => ({
        ...existing,
        score: calculateCompanySecretarySimilarityScore(importedSecretary, existing),
      }))
      .filter((match) => match.score >= 50)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  };

  const performImport = async (secretariesToImport) => {

    try {
      setImporting(true);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const secretary of secretariesToImport) {
        try {
          if (secretary.resolution?.action === 'skip'
            || secretary.resolution?.action?.startsWith('map-')) {
            continue;
          }
          if (!secretary.resolution && secretary.status === 'duplicate') {
            continue;
          }

          const response = await csAdminApi.createCompanySecretary(
            buildCompanySecretaryCreatePayload(secretary)
          );
          if (response.status !== 201 && response.status !== 200) {
            throw new Error(response.data?.error?.message || response.data?.message || 'Create failed');
          }
          if (response.data?.success === false) {
            throw new Error(response.data?.error?.message || response.data?.message || 'Create failed');
          }
          successCount += 1;
        } catch (err) {
          const label = `${secretary.firstName} ${secretary.lastName}`.trim() || secretary.email;
          const errorMsg = err.response?.data?.error?.message
            || err.response?.data?.message
            || err.message
            || 'Unknown error';
          errors.push(`${label}: ${errorMsg}`);
          errorCount += 1;
        }
      }

      if (successCount > 0) {
        enqueueSnackbar(`Successfully imported ${successCount} company secretary account(s)`, { variant: 'success' });
        fetchSecretaries();
      }
      if (errorCount > 0) {
        enqueueSnackbar(`Failed to import ${errorCount} account(s). Check console for details.`, { variant: 'error' });
        errors.forEach((entry) => console.error('  -', entry));
      }

      setImportModalOpen(false);
      setImportedSecretaries([]);
    } catch (err) {
      enqueueSnackbar(`Error during import: ${err.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    try {
      setImporting(true);
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet);

          if (rows.length === 0) {
            enqueueSnackbar('No data found in the file', { variant: 'warning' });
            setImporting(false);
            return;
          }

          let companiesMap = {};
          let existingSecretaries = [];
          {
            try {
              const [companiesRes, secretariesRes] = await Promise.all([
                csAdminApi.getCompanies(),
                csAdminApi.getCompanySecretaries(),
              ]);
              (companiesRes.data?.data || []).map(mapCompanyFromApi).forEach((company) => {
                companiesMap[company.name.toLowerCase().trim()] = company.id;
              });
              existingSecretaries = (secretariesRes.data?.data || []).map(mapCompanySecretaryFromApi);
            } catch (err) {
              enqueueSnackbar('Failed to load reference data. Please try again.', { variant: 'error' });
              setImporting(false);
              return;
            }
          }

          const processedSecretaries = [];
          for (let i = 0; i < rows.length; i++) {
            const parsed = parseImportedCompanySecretaryRow(rows[i], i);
            if (isEmptyCompanySecretaryRow(parsed) || isCompanySecretaryTemplateHelperRow(parsed)) {
              continue;
            }

            const assignmentErrors = [];
            const assignedCompanyIds = [];
            if (parsed.assignedCompanyNames.length) {
              parsed.assignedCompanyNames.forEach((companyName) => {
                const id = companiesMap[companyName.toLowerCase().trim()];
                if (id) {
                  assignedCompanyIds.push(id);
                } else {
                  assignmentErrors.push(`Company "${companyName}" not found`);
                }
              });
            }
            parsed.assignedCompanyIds = assignedCompanyIds;

            const validation = validateImportedCompanySecretaryRow(parsed);

            let status = 'ready';
            let errors = [];
            let matches = [];

            if (!validation.isValid) {
              status = 'error';
              errors = validation.errors;
            } else if (assignmentErrors.length) {
              status = 'error';
              errors = assignmentErrors;
            } else {
              matches = findMatchingSecretaries(parsed, existingSecretaries);
              if (matches.length > 0) {
                status = 'duplicate';
              }
            }

            processedSecretaries.push({ ...parsed, status, errors, matches });
          }

          if (processedSecretaries.length === 0) {
            enqueueSnackbar('No company secretary rows found in the file', { variant: 'warning' });
            setImporting(false);
            return;
          }

          const hasIssues = processedSecretaries.some((s) => s.status !== 'ready');
          if (hasIssues) {
            setImportedSecretaries(processedSecretaries);
            setImportModalOpen(true);
          } else {
            await performImport(processedSecretaries);
          }
        } catch (err) {
          enqueueSnackbar('Error processing file. Please check the format.', { variant: 'error' });
        } finally {
          setImporting(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      enqueueSnackbar('Error reading file', { variant: 'error' });
      setImporting(false);
    }
  };

  const columns = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 220,
      render: (row) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                bgcolor: '#E3F2FD',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonIcon sx={{ fontSize: 24, color: '#1976D2' }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.name}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 220,
    },
    {
      id: 'phone',
      label: 'Phone',
      minWidth: 150,
    },
    {
      id: 'companies',
      label: 'Assigned Companies',
      minWidth: 220,
      render: (row) => {
        const names = row.companies || [];
        if (!names.length) return '-';
        return (
          <Typography variant="body2" color="text.secondary">
            {names.join(', ')}
          </Typography>
        );
      },
    },
    {
      id: 'lastLogin',
      label: 'Last Login',
      minWidth: 180,
      render: (row) => {
        if (!row.lastLogin) return '-';
        const date = new Date(row.lastLogin);
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      render: (row) => (
        <Chip
          label={row.status}
          sx={{
            ...getStatusChipSx(row.status),
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
            borderRadius: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
          size="small"
        />
      ),
    },
  ];

  const companyFilterOptions = useMemo(() => {
    const names = new Set();
    secretaries.forEach((secretary) => {
      (secretary.companies || []).forEach((companyName) => {
        if (companyName) names.add(companyName);
      });
    });
    return [
      { value: '', label: 'All Companies' },
      ...Array.from(names).sort().map((name) => ({ value: name, label: name })),
    ];
  }, [secretaries]);

  const filterOptions = [
    {
      label: 'Company',
      name: 'company',
      value: companyFilter,
      options: companyFilterOptions,
    },
    {
      label: 'Status',
      name: 'status',
      value: statusFilter,
      options: [
        { value: '', label: 'All Status' },
        { value: 'ACTIVE', label: 'Active' },
        { value: 'INACTIVE', label: 'Inactive' },
      ],
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Company Secretaries Pool
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage company secretary accounts and their assignments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
            disabled={importing}
          >
            Import Secretaries
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
            />
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/company-secretaries/create')}
          >
            Create CS Account
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Total Company Secretaries"
            value={summary.total}
            icon={AdminIcon}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Active Secretaries"
            value={summary.active}
            icon={CheckCircleIcon}
            color={ACTIVE_GREEN}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Total Assignments"
            value={summary.totalAssignments}
            icon={BusinessIcon}
            color="#FF9800"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <FilterPanel
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search by name, email, or assigned company..."
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Secretaries Table */}
      <DataTable
        rows={filteredSecretaries}
        columns={columns}
        onRowClick={(row) => navigate(`/company-secretaries/${row.id}`)}
        actions={[
          {
            label: 'Edit Permissions',
            icon: <SecurityIcon fontSize="small" />,
            onClick: (row) => {
              if (!canManagePortalAccountPermissions(user?.role, row.role)) {
                enqueueSnackbar('You do not have permission to edit this account.', { variant: 'warning' });
                return;
              }
              setSelectedSecretary(row);
              setEditPermissionsOpen(true);
            },
          },
        ]}
        emptyMessage="No company secretaries found"
      />

      <EditGlobalPermissionsDialog
        open={editPermissionsOpen}
        principal={selectedSecretary}
        principalType="portal_account"
        onClose={() => {
          setEditPermissionsOpen(false);
          setSelectedSecretary(null);
        }}
        onSave={async ({ useCustom, matrix }) => {
          try {
            if (selectedSecretary?.id) {
              await csAdminApi.updatePortalAccountPermissions(selectedSecretary.id, { useCustom, matrix });
              enqueueSnackbar('Permissions saved', { variant: 'success' });
            }
          } catch (err) {
            console.error('Save permissions failed:', err);
            enqueueSnackbar('Failed to save permissions', { variant: 'error' });
            return;
          }
          setEditPermissionsOpen(false);
          setSelectedSecretary(null);
        }}
      />

      <CompanySecretaryImportReviewModal
        open={importModalOpen}
        onClose={() => {
          setImportModalOpen(false);
          setImportedSecretaries([]);
        }}
        importedSecretaries={importedSecretaries}
        onImport={performImport}
      />
    </Container>
  );
};

export default CompanySecretariesList;
